// Mount the Drive package (#216): build its route from what it needs from this
// app — the generic host (modals, the signed-in user and permissions:
// host-base.ts), the shared file preview, the paged-list query hook, the error
// page (inline for a failing shared link, and as the route's errorElement) and
// the employee and department selects of the share dialog. App.tsx mounts the
// route from HERE, so the package cannot be mounted unregistered. The HTTP
// client goes through core's registry (utils/axios/register-core.ts).
//
// Drive has no locale namespace of its own (its strings are the app's global
// keys) and no link to another module's page, so there is no `i18n` or `links`
// member.
import { createDriveModule, type DriveHost } from '@vnetwork/datacentral-drive'
import { hostBase } from '@/app/host-base'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import ErrorPageWithRoute from '@/components/ErrorBoundary/ErrorPageWithRoute'
import { useFilePreview } from '@/contexts/file-preview'
import { useInfiniteQueryWithHandler } from '@/hooks/useInfiniteQueryWithHandler'
import { lazy, Suspense } from 'react'

// Lazy: the two selects only render inside the share dialog. Imported eagerly
// here they pull their loaders into the entry chunk; before the move they
// loaded with the Drive route chunk (#212).
const LazyEmployeeSelect = lazy(
    () => import('@/components/Form/Shared/EmployeeSelect')
)
const LazyDepartmentSelect = lazy(
    () => import('@/components/Form/Shared/DepartmentSelect')
)
const EmployeeSelect: DriveHost['EmployeeSelect'] = (props) => (
    <Suspense fallback={null}>
        <LazyEmployeeSelect {...props} />
    </Suspense>
)
const DepartmentSelect: DriveHost['DepartmentSelect'] = (props) => (
    <Suspense fallback={null}>
        <LazyDepartmentSelect {...props} />
    </Suspense>
)

// The package does not render the app's ErrorPageWithRoute; the host adds it as
// the route's errorElement, as for Warehouse (#59), Dayoffs (#204) and Business.
export const DriveRoute = {
    ...createDriveModule({
        ...hostBase,
        useFilePreview,
        useInfiniteQueryWithHandler,
        ErrorPage,
        EmployeeSelect,
        DepartmentSelect,
    }),
    errorElement: <ErrorPageWithRoute />,
}
